/**
 * Utilidades para manejo de fechas en la aplicación
 * Evita problemas de zona horaria al procesar fechas de la base de datos
 */

/**
 * Convierte una fecha del formato de la BD (YYYY-MM-DD) a un objeto Date local
 * @param {string} dateString - Fecha en formato "YYYY-MM-DD"
 * @returns {Date} - Objeto Date interpretado en zona horaria local
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;

  // Agregar tiempo para forzar interpretación local
  return new Date(dateString + "T00:00:00");
};

/**
 * Formatea una fecha para mostrar en la interfaz
 * @param {string} dateString - Fecha en formato "YYYY-MM-DD"
 * @param {string} locale - Locale para formateo (por defecto es-ES)
 * @returns {string} - Fecha formateada
 */
export const formatDisplayDate = (dateString, locale = "es-ES") => {
  const date = parseLocalDate(dateString);
  if (!date) return "";

  return date.toLocaleDateString(locale);
};

/**
 * Formatea una fecha/timestamp completo para mostrar en la interfaz
 * @param {string} dateString - Fecha/timestamp ISO
 * @param {string} locale - Locale para formateo (por defecto es-ES)
 * @returns {string} - Fecha formateada
 */
export const formatTimestampDate = (dateString, locale = "es-ES") => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleDateString(locale);
};

/**
 * Compara dos fechas ignorando la hora
 * @param {string|Date} date1 - Primera fecha
 * @param {string|Date} date2 - Segunda fecha
 * @returns {number} - Diferencia en días (positivo si date1 > date2)
 */
export const compareDatesOnly = (date1, date2) => {
  const d1 = typeof date1 === "string" ? parseLocalDate(date1) : date1;
  const d2 = typeof date2 === "string" ? parseLocalDate(date2) : date2;

  if (!d1 || !d2) return 0;

  // Normalizar a medianoche para comparación
  const date1Only = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const date2Only = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());

  const timeDifference = date1Only.getTime() - date2Only.getTime();
  return Math.ceil(timeDifference / (1000 * 3600 * 24));
};

/**
 * Verifica si una fecha/hora de evento sigue activa (hasta 12 horas después de la hora del evento)
 * @param {string} eventDate - Fecha del evento en formato "YYYY-MM-DD"
 * @param {string} eventTime - Hora del evento en formato "HH:MM"
 * @returns {boolean} - True si el evento sigue activo
 */
export const isEventStillActive = (eventDate, eventTime) => {
  if (!eventDate) return false;
  
  const currentDateTime = new Date();
  
  // Si no hay hora específica, utilizamos el comportamiento anterior (2 días)
  if (!eventTime) {
    const daysDifference = compareDatesOnly(currentDateTime, eventDate);
    return daysDifference <= 2;
  }
  
  // Creamos un objeto Date con la fecha y hora del evento
  const [hours, minutes] = eventTime.split(':').map(Number);
  const eventDateTime = parseLocalDate(eventDate);
  eventDateTime.setHours(hours, minutes, 0, 0);
  
  // Calculamos la fecha/hora de expiración (12 horas después del evento)
  const expirationDateTime = new Date(eventDateTime);
  expirationDateTime.setHours(eventDateTime.getHours() + 12);
  
  // El evento está activo si la fecha/hora actual es anterior a la expiración
  return currentDateTime <= expirationDateTime;
};

/**
 * Obtiene el número de horas hasta la expiración del evento
 * @param {string} eventDate - Fecha del evento en formato "YYYY-MM-DD"
 * @param {string} eventTime - Hora del evento en formato "HH:MM"
 * @returns {number} - Horas restantes (negativo si ya expiró)
 */
export const getTimeUntilEventExpiration = (eventDate, eventTime) => {
  if (!eventDate) return -999;
  
  const currentDateTime = new Date();
  
  // Si no hay hora específica, utilizamos el comportamiento anterior (2 días)
  if (!eventTime) {
    const eventDateTime = parseLocalDate(eventDate);
    const expirationDate = new Date(eventDateTime);
    expirationDate.setDate(expirationDate.getDate() + 2);
    
    const timeDifference = expirationDate.getTime() - currentDateTime.getTime();
    return Math.ceil(timeDifference / (1000 * 3600 * 24));
  }
  
  // Creamos un objeto Date con la fecha y hora del evento
  const [hours, minutes] = eventTime.split(':').map(Number);
  const eventDateTime = parseLocalDate(eventDate);
  eventDateTime.setHours(hours, minutes, 0, 0);
  
  // Calculamos la fecha/hora de expiración (12 horas después del evento)
  const expirationDateTime = new Date(eventDateTime);
  expirationDateTime.setHours(eventDateTime.getHours() + 12);
  
  // Calculamos la diferencia en horas
  const timeDifference = expirationDateTime.getTime() - currentDateTime.getTime();
  return Math.ceil(timeDifference / (1000 * 3600));
};

/**
 * Obtiene el número de días hasta la expiración del evento (mantiene compatibilidad)
 * @param {string} eventDate - Fecha del evento en formato "YYYY-MM-DD"
 * @param {string} eventTime - Hora del evento en formato "HH:MM" (opcional)
 * @returns {number} - Días restantes (negativo si ya expiró)
 * @deprecated Use getTimeUntilEventExpiration for more precise expiration calculation
 */
export const getDaysUntilEventExpiration = (eventDate, eventTime) => {
  if (!eventTime) {
    // Comportamiento original para mantener compatibilidad
    if (!eventDate) return -999;
  
    const eventDateTime = parseLocalDate(eventDate);
    const currentDate = new Date();
  
    // Agregar 2 días a la fecha del evento para calcular expiración
    const expirationDate = new Date(eventDateTime);
    expirationDate.setDate(expirationDate.getDate() + 2);
  
    const timeDifference = expirationDate.getTime() - currentDate.getTime();
    return Math.ceil(timeDifference / (1000 * 3600 * 24));
  }
  
  // Convertimos horas a días (aproximadamente)
  const hoursLeft = getTimeUntilEventExpiration(eventDate, eventTime);
  return Math.ceil(hoursLeft / 24);
};
